export interface GFFFeature {
    seqid: string;
    source: string;
    type: string;
    start: number;
    end: number;
    score: string;
    strand: '+' | '-' | '.' | '?';
    phase: string;
    attributes: Record<string, string>;
    attributeString: string;
    parents: string[];
}

export interface ParsedGFF {
    features: GFFFeature[];
    byId: Map<string, GFFFeature>;
    childrenByParent: Map<string, GFFFeature[]>;
}

const decode = (raw: string): string => {
    try {
        return decodeURIComponent(raw);
    } catch {
        return raw;
    }
};

const parseAttributes = (col9: string): Record<string, string> => {
    const out: Record<string, string> = {};
    if (!col9 || col9 === '.') return out;
    for (const pair of col9.split(';')) {
        const trimmed = pair.trim();
        if (!trimmed) continue;
        const eq = trimmed.indexOf('=');
        if (eq < 0) continue;
        const key = decode(trimmed.slice(0, eq).trim());
        const value = decode(trimmed.slice(eq + 1).trim());
        if (key) out[key] = value;
    }
    return out;
};

const STRAND_RE = /^[+\-.?]$/;

export const parseGFF = (text: string): ParsedGFF => {
    const features: GFFFeature[] = [];
    const byId = new Map<string, GFFFeature>();
    const childrenByParent = new Map<string, GFFFeature[]>();

    for (const rawLine of text.split(/\r?\n/)) {
        if (!rawLine || rawLine.startsWith('#')) continue;
        const cols = rawLine.split('\t');
        if (cols.length < 9) continue;

        const start = Number(cols[3]);
        const end = Number(cols[4]);
        if (Number.isNaN(start) || Number.isNaN(end)) continue;

        const strandRaw = cols[6];
        const strand: GFFFeature['strand'] = STRAND_RE.test(strandRaw) ? (strandRaw as GFFFeature['strand']) : '.';
        const attributes = parseAttributes(cols[8]);
        const parents = attributes.Parent ? attributes.Parent.split(',').map((p) => p.trim()).filter(Boolean) : [];

        const feature: GFFFeature = {
            seqid: cols[0],
            source: cols[1],
            type: cols[2],
            start,
            end,
            score: cols[5],
            strand,
            phase: cols[7],
            attributes,
            attributeString: cols[8],
            parents,
        };

        features.push(feature);
        if (attributes.ID) byId.set(attributes.ID, feature);
        for (const parentId of parents) {
            const bucket = childrenByParent.get(parentId);
            if (bucket) bucket.push(feature);
            else childrenByParent.set(parentId, [feature]);
        }
    }

    return { features, byId, childrenByParent };
};

/**
 * Walk Parent chain bottom-up. Returns [self, parent, grandparent, ...].
 * Cycles are guarded via a visited set.
 */
export const buildAncestry = (feature: GFFFeature, byId: Map<string, GFFFeature>): GFFFeature[] => {
    const chain: GFFFeature[] = [feature];
    const visited = new Set<string>();
    if (feature.attributes.ID) visited.add(feature.attributes.ID);

    let cursor: GFFFeature | undefined = feature;
    while (cursor && cursor.parents.length > 0) {
        const next = byId.get(cursor.parents[0]);
        if (!next) break;
        const nextId = next.attributes.ID;
        if (nextId && visited.has(nextId)) break;
        if (nextId) visited.add(nextId);
        chain.push(next);
        cursor = next;
    }
    return chain;
};

type PopoverEntry = string | { name?: string; value?: string | number };

interface PopoverBlock {
    type?: string;
    id?: string;
    name?: string;
    seqid?: string;
    start?: number;
    end?: number;
}

/**
 * IGV packs several overlapping features into one popoverData array separated by
 * '<hr/>' marker strings. Split into per-feature blocks so each can be matched
 * independently against the parsed GFF.
 */
const splitPopoverBlocks = (popoverData: PopoverEntry[]): PopoverBlock[] => {
    const blocks: PopoverBlock[] = [];
    let current: PopoverBlock = {};

    const flush = () => {
        if (Object.keys(current).length > 0) blocks.push(current);
        current = {};
    };

    for (const entry of popoverData) {
        if (typeof entry === 'string') {
            // '<hr/>' or other separator marker
            flush();
            continue;
        }
        if (!entry?.name) continue;
        const key = entry.name.replace(/:$/, '').trim().toLowerCase();
        const value = (entry.value ?? '').toString();
        if (key === 'type') current.type = value;
        else if (key === 'id') current.id = value;
        else if (key === 'name') current.name = value;
        else if (key === 'location') {
            const [chr, range] = value.split(':');
            current.seqid = chr ?? '';
            if (range) {
                const [s, e] = range.split('-').map((p) => Number(p.replace(/,/g, '')));
                if (!Number.isNaN(s)) current.start = s;
                if (!Number.isNaN(e)) current.end = e;
            }
        }
    }
    flush();
    return blocks;
};

/**
 * Find the most specific feature among everything IGV reported under the click.
 *
 * Resolution order for each block: explicit ID → Name → (type + seqid + start + end).
 * Across blocks we pick the feature with the deepest Parent chain — so when the
 * user clicks where `gene`, `mRNA` and `exon` all stack, we return the exon and
 * the UI can render `gene → mRNA → exon`.
 */
export const findFeatureFromPopover = (
    parsed: ParsedGFF,
    popoverData: PopoverEntry[]
): GFFFeature | null => {
    const blocks = splitPopoverBlocks(popoverData);
    const candidates: GFFFeature[] = [];
    const seen = new Set<GFFFeature>();

    for (const block of blocks) {
        let match: GFFFeature | undefined;

        if (block.id) match = parsed.byId.get(block.id);
        if (!match && block.name) match = parsed.byId.get(block.name);
        if (!match && block.type && block.seqid && block.start != null && block.end != null) {
            match = parsed.features.find(
                (f) =>
                    f.type === block.type &&
                    f.seqid === block.seqid &&
                    f.start === block.start &&
                    f.end === block.end
            );
        }

        if (match && !seen.has(match)) {
            candidates.push(match);
            seen.add(match);
        }
    }

    if (candidates.length === 0) return null;

    let best = candidates[0];
    let bestDepth = buildAncestry(best, parsed.byId).length;
    for (let i = 1; i < candidates.length; i++) {
        const depth = buildAncestry(candidates[i], parsed.byId).length;
        if (depth > bestDepth) {
            best = candidates[i];
            bestDepth = depth;
        }
    }
    return best;
};
